import React, { useState, useEffect } from 'react';
import { Sale, SaleReturn, ReturnType } from '../../utils/types';
import { currencyFormat } from '../../utils/constants';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
// يجب تثبيت مكتبة recharts باستخدام الأمر: npm install recharts
// لتفعيل الرسوم البيانية، قم بتثبيت المكتبة وإلغاء تعليق الأسطر التالية
// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   Tooltip,
//   Legend,
//   ResponsiveContainer,
//   PieChart,
//   Pie,
//   Cell
// } from 'recharts';

// تعريف مؤقت لمكونات recharts
// تعريف مؤقت لمكونات recharts مع دعم جميع الخصائص
const BarChart = (props: any) => (
  <div style={{ border: '1px dashed #ccc', padding: '20px', textAlign: 'center', marginBottom: '20px' }}>
    <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>الرسم البياني للمبيعات</div>
    <div style={{ color: '#666' }}>يجب تثبيت مكتبة recharts لعرض الرسوم البيانية</div>
    {props.children}
  </div>
);
const Bar = (props: any) => <div style={{ display: 'none' }}>{props.dataKey}</div>;
const XAxis = (props: any) => <div style={{ display: 'none' }}>{props.dataKey}</div>;
const YAxis = (props: any) => <div style={{ display: 'none' }}></div>;
const CartesianGrid = (props: any) => <div style={{ display: 'none' }}></div>;
const Tooltip = (props: any) => <div style={{ display: 'none' }}>{props.formatter ? 'Tooltip' : ''}</div>;
const Legend = (props: any) => <div style={{ display: 'none' }}></div>;
const ResponsiveContainer = (props: any) => <div>{props.children}</div>;
const PieChart = (props: any) => (
  <div style={{ border: '1px dashed #ccc', padding: '20px', textAlign: 'center', marginBottom: '20px' }}>
    <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>الرسم الدائري للمبيعات</div>
    <div style={{ color: '#666' }}>يجب تثبيت مكتبة recharts لعرض الرسوم البيانية</div>
    {props.children}
  </div>
);
const Pie = (props: any) => <div style={{ display: 'none' }}>{props.dataKey}</div>;
const Cell = (props: any) => <div style={{ display: 'none' }}></div>;

// تم نقل الاستيرادات إلى أعلى الملف

interface SalesStatisticsProps {
  sales: Sale[];
  returns: SaleReturn[];
  startDate?: Date | null;
  endDate?: Date | null;
  loading: boolean;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const SalesStatistics: React.FC<SalesStatisticsProps> = ({
  sales,
  returns,
  startDate,
  endDate,
  loading
}) => {
  const [stats, setStats] = useState({
    totalSales: 0,
    totalAmount: 0,
    averageSaleAmount: 0,
    totalReturns: 0,
    totalReturnAmount: 0,
    returnRate: 0,
    salesByPaymentMethod: [] as { name: string; value: number }[],
    salesByDay: [] as { name: string; sales: number }[],
    topSellingItems: [] as { name: string; quantity: number; total: number }[]
  });

  useEffect(() => {
    if (loading) return;

    // حساب الإحصائيات
    calculateStatistics();
  }, [sales, returns, loading]);

  const calculateStatistics = () => {
    // إجمالي المبيعات
    const totalSales = sales.length;
    const totalAmount = sales.reduce((sum, sale) => sum + sale.grandTotal, 0);
    const averageSaleAmount = totalSales > 0 ? totalAmount / totalSales : 0;

    // إجمالي المرتجعات
    const totalReturns = returns.length;
    const totalReturnAmount = returns.reduce((sum, ret) => sum + ret.totalAmount, 0);
    const returnRate = totalSales > 0 ? (totalReturns / totalSales) * 100 : 0;

    // المبيعات حسب طريقة الدفع
    const paymentMethodsMap = new Map<string, number>();
    sales.forEach(sale => {
      const method = getPaymentMethodName(sale.paymentMethod);
      paymentMethodsMap.set(method, (paymentMethodsMap.get(method) || 0) + sale.grandTotal);
    });
    const salesByPaymentMethod = Array.from(paymentMethodsMap.entries()).map(([name, value]) => ({
      name,
      value
    }));

    // المبيعات حسب اليوم
    const salesByDayMap = new Map<string, number>();
    sales.forEach(sale => {
      const date = new Date(sale.createdAt).toLocaleDateString('ar-EG');
      salesByDayMap.set(date, (salesByDayMap.get(date) || 0) + sale.grandTotal);
    });
    const salesByDay = Array.from(salesByDayMap.entries())
      .map(([name, sales]) => ({ name, sales }))
      .sort((a, b) => {
        const dateA = new Date(a.name.split('/').reverse().join('-'));
        const dateB = new Date(b.name.split('/').reverse().join('-'));
        return dateA.getTime() - dateB.getTime();
      })
      .slice(-10); // آخر 10 أيام فقط

    // أكثر المنتجات مبيعًا
    const itemsMap = new Map<string, { name: string; quantity: number; total: number }>();
    sales.forEach(sale => {
      sale.items.forEach(item => {
        if (itemsMap.has(item.itemId)) {
          const existingItem = itemsMap.get(item.itemId)!;
          existingItem.quantity += item.quantity;
          existingItem.total += item.total;
        } else {
          itemsMap.set(item.itemId, {
            name: item.name,
            quantity: item.quantity,
            total: item.total
          });
        }
      });
    });
    const topSellingItems = Array.from(itemsMap.values())
      .sort((a, b) => b.total - a.total)
      .slice(0, 5); // أعلى 5 منتجات

    setStats({
      totalSales,
      totalAmount,
      averageSaleAmount,
      totalReturns,
      totalReturnAmount,
      returnRate,
      salesByPaymentMethod,
      salesByDay,
      topSellingItems
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* بطاقات الإحصائيات */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                إجمالي المبيعات
              </Typography>
              <Typography variant="h4">{stats.totalSales}</Typography>
              <Typography variant="body2" color="textSecondary">
                بقيمة {currencyFormat(stats.totalAmount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                متوسط قيمة الفاتورة
              </Typography>
              <Typography variant="h4">{currencyFormat(stats.averageSaleAmount)}</Typography>
              <Typography variant="body2" color="textSecondary">
                لعدد {stats.totalSales} فاتورة
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                المرتجعات
              </Typography>
              <Typography variant="h4">{stats.totalReturns}</Typography>
              <Typography variant="body2" color="textSecondary">
                بنسبة {stats.returnRate.toFixed(1)}% من المبيعات
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* الرسوم البيانية */}
      <Grid container spacing={3}>
        {/* المبيعات حسب اليوم */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              المبيعات حسب اليوم
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={stats.salesByDay}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value: any) => currencyFormat(value as number)} />
                  <Legend />
                  <Bar dataKey="sales" name="المبيعات" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* المبيعات حسب طريقة الدفع */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              المبيعات حسب طريقة الدفع
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stats.salesByPaymentMethod}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }: { name: string, percent: number }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {stats.salesByPaymentMethod.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: any) => currencyFormat(value as number)} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* أكثر المنتجات مبيعًا */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              أكثر المنتجات مبيعًا
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>المنتج</TableCell>
                    <TableCell align="center">الكمية المباعة</TableCell>
                    <TableCell align="center">إجمالي المبيعات</TableCell>
                    <TableCell align="center">النسبة من إجمالي المبيعات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.topSellingItems.map((item) => (
                    <TableRow key={item.name}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell align="center">{item.quantity}</TableCell>
                      <TableCell align="center">{currencyFormat(item.total)}</TableCell>
                      <TableCell align="center">
                        {stats.totalAmount > 0
                          ? `${((item.total / stats.totalAmount) * 100).toFixed(1)}%`
                          : '0%'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* إحصائيات المرتجعات */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              إحصائيات المرتجعات
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    إجمالي المرتجعات
                  </Typography>
                  <Typography variant="h6">{stats.totalReturns}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    قيمة المرتجعات
                  </Typography>
                  <Typography variant="h6">{currencyFormat(stats.totalReturnAmount)}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    نسبة المرتجعات من المبيعات
                  </Typography>
                  <Typography variant="h6">{stats.returnRate.toFixed(1)}%</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    نسبة قيمة المرتجعات
                  </Typography>
                  <Typography variant="h6">
                    {stats.totalAmount > 0
                      ? `${((stats.totalReturnAmount / stats.totalAmount) * 100).toFixed(1)}%`
                      : '0%'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" gutterBottom>
              المرتجعات حسب النوع
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    مرتجعات كاملة
                  </Typography>
                  <Typography variant="h6">
                    {returns.filter(ret => ret.returnType === ReturnType.FULL).length}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    مرتجعات جزئية
                  </Typography>
                  <Typography variant="h6">
                    {returns.filter(ret => ret.returnType === ReturnType.PARTIAL).length}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

/**
 * الحصول على اسم طريقة الدفع بالعربية
 */
const getPaymentMethodName = (method: string): string => {
  switch (method) {
    case 'cash': return 'نقدًا';
    case 'card': return 'بطاقة ائتمان';
    case 'bankTransfer': return 'تحويل بنكي';
    case 'stc': return 'STC Pay';
    case 'mada': return 'مدى';
    default: return method;
  }
};

export default SalesStatistics;
